import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Badge
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import useWebSocket from '../../hooks/useWebSocket';
import { alarmsAPI, alertsAPI } from '../../services/api';

const Alarms = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [alarms, setAlarms] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [newAlarmsCount, setNewAlarmsCount] = useState(0);
  
  const { readyState, connectionError } = useWebSocket(null, {
    onMessage: (data) => {
      if (data.type === 'NEW_ALARM' && data.tenantId === user?.tenant_id) {
        setNewAlarmsCount(prev => prev + 1);
        loadAlarms();
        loadStats();
      }
    }
  });

  useEffect(() => {
    if (user?.tenant_id) {
      loadAlarms();
      loadStats();
      
      const interval = setInterval(() => {
        loadAlarms();
        loadStats();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadAlarms = async () => {
    try {
      const response = await alarmsAPI.getAll();
      setAlarms(response.data?.data || []);
    } catch (error) {
      console.error('Error loading alarms:', error);
      setAlarms([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await alertsAPI.getStats();
      const data = response.data?.data || {};
      // Map notifications stats to alarms display format
      setStats({
        total: data.total_alerts_today || 0,
        critical: data.critical_alerts || 0,
        expired: data.pending_alerts || 0,
        expiring: data.acknowledged_alerts || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ total: 0, critical: 0, expired: 0, expiring: 0 });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VENCIDO': return 'error';
      case 'VENCENDO': return 'warning';
      default: return 'success';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'critical': return 'error';
      case 'normal': return 'warning';
      default: return 'info';
    }
  };

  const translateAlarmType = (alarmType) => {
    switch (alarmType) {
      case 'EQUIPMENT_EXPIRED': return t('alarms.equipmentExpired');
      case 'EQUIPMENT_EXPIRING': return t('alarms.equipmentExpiring');
      default: return alarmType;
    }
  };

  const acknowledgeAlarm = async (alarmId) => {
    try {
      await alarmsAPI.acknowledge(alarmId);
      loadAlarms();
      loadStats();
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          🚨 {t('alarms.title')}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label={readyState === 1 ? t('alarms.connected') : t('alarms.disconnected')}
            color={readyState === 1 ? 'success' : 'error'}
            size="small"
          />
          <Badge badgeContent={newAlarmsCount} color="error">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadAlarms();
                loadStats();
                setNewAlarmsCount(0);
              }}
            >
              {t('alarms.refresh')}
            </Button>
          </Badge>
        </Box>
      </Box>

      {connectionError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          WebSocket Error: {connectionError}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.total}</Typography>
                  <Typography variant="body2" color="textSecondary">{t('alarms.total')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.critical}</Typography>
                  <Typography variant="body2" color="textSecondary">{t('alarms.critical')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.expired}</Typography>
                  <Typography variant="body2" color="textSecondary">{t('alarms.expired')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.expiring}</Typography>
                  <Typography variant="body2" color="textSecondary">{t('alarms.expiring')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('alarms.activeAlarms')}
          </Typography>

          {alarms.length === 0 ? (
            <Alert severity="success" icon={<CheckIcon />}>
              {t('alarms.noAlarms')}
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('alarms.equipment')}</TableCell>
                    <TableCell>{t('alarms.severity')}</TableCell>
                    <TableCell>{t('alarms.category')}</TableCell>
                    <TableCell>{t('alarms.validationDate')}</TableCell>
                    <TableCell>{t('alarms.barrier')}</TableCell>
                    <TableCell>{t('alarms.timestamp')}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alarms.map((alarm) => (
                    <TableRow key={alarm.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {alarm.equipment_name || alarm.title || 'Alarme'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {alarm.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={alarm.severity || 'MEDIUM'}
                          color={alarm.severity === 'CRITICAL' ? 'error' : alarm.severity === 'HIGH' ? 'warning' : 'info'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(alarm.category || 'normal').toUpperCase()}
                          color={getCategoryColor(alarm.category || 'normal')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {alarm.validation_date ? new Date(alarm.validation_date).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell>{alarm.barrier_names || translateAlarmType(alarm.alarm_type) || '-'}</TableCell>
                      <TableCell>
                        {alarm.created_at ? new Date(alarm.created_at).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => acknowledgeAlarm(alarm.id)}
                          disabled={alarm.status === 'ACKNOWLEDGED'}
                        >
                          {alarm.status === 'ACKNOWLEDGED' ? t('alarms.acknowledged') : t('alarms.acknowledge')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Alarms;